import { cn } from '@workspace/lib';
import Link from 'next/link';
import { Icon, Picture, Typography } from '@workspace/ui/common';
import { pageApplicationsVariants } from '@workspace/blocks/PageApplications/applications.variants';
import type { ApplicationCard, ApplicationItemProps, ConfiguredApplicationItem } from '../application.types';
import { getApplicationImageDimensions, LEGACY_APPLICATION_IMAGE_DIMENSIONS } from '../layout';

const isConfiguredItem = (item: ApplicationItemProps['item']): item is ConfiguredApplicationItem => 'reference' in item;

const resolveItem = (item: ApplicationItemProps['item']) => {
  if (!isConfiguredItem(item)) {
    return { card: item, isConfigured: false, linkText: 'Read More', tag: item.tag };
  }

  return {
    card: item.reference,
    isConfigured: true,
    linkText: item.linkText || 'Read More',
    tag: item.tag || item.reference?.tag,
  };
};

const resolveHref = (item: ApplicationCard | null | undefined) => {
  if (!item) return undefined;
  if (item.url) return item.url;
  if (!item.slug?.current || !item.type) return undefined;

  const routeType = item.type === 'blogs' ? 'blog' : item.type;
  return `/${routeType}/${item.slug.current}`;
};

export function ApplicationItem({ item, className = '', noLinkWrapper, itemCount }: ApplicationItemProps) {
  const { card: cardData, isConfigured, linkText, tag } = resolveItem(item);
  const href = resolveHref(cardData);
  const imageDimensions = isConfigured ? getApplicationImageDimensions(itemCount) : LEGACY_APPLICATION_IMAGE_DIMENSIONS;
  const {
    card: cardClass,
    media,
    picture,
    image,
    cardBody,
    cardText,
    tag: tagClass,
    cardTitle,
    cardDescription,
    cardActions,
    primaryAction,
    primaryActionText,
    primaryActionIcon,
    campaignLink,
  } = pageApplicationsVariants({ itemCount });

  if (!href || noLinkWrapper) return null;

  return (
    <article className={cn('group relative', cardClass(), className)}>
      <div className={cn('overflow-hidden', media())}>
        {cardData?.thumbnail && (
          <Picture
            image={cardData.thumbnail}
            desktopWidth={imageDimensions.desktop.width}
            desktopHeight={imageDimensions.desktop.height}
            mobileWidth={imageDimensions.mobile.width}
            mobileHeight={imageDimensions.mobile.height}
            className={cn('size-full', picture())}
            imgClassName={cn(
              'object-cover transition-transform duration-300 ease-in-out group-hover:scale-[1.03]',
              image(),
            )}
          />
        )}
      </div>

      <div className={cn('flex flex-col justify-between', cardBody())}>
        <div className={cn('flex flex-col', cardText())}>
          {tag && (
            <Typography variant="bodyXs" className={tagClass()}>
              {tag}
            </Typography>
          )}
          <Typography variant="h3" className={cardTitle()} title={cardData?.title} styleVariant="h6">
            {cardData?.title}
          </Typography>

          <Typography variant="bodySm" className={cardDescription()} title={cardData?.shortDescription}>
            {cardData?.shortDescription}
          </Typography>
        </div>

        <div className={cn('flex items-center', cardActions())}>
          <Link href={href} className={cn('flex items-center', primaryAction(), 'after:absolute after:inset-0')}>
            <span className={cn('inline-flex items-center', primaryActionText())}>{linkText}</span>
            <Icon name="arrowRightIcon" size={24} className={primaryActionIcon()} />
          </Link>

          {cardData?.campaign?.url && (
            <Link
              href={cardData.campaign.url}
              target={cardData.campaign.type === 'external' ? '_blank' : undefined}
              className={cn(campaignLink(), 'relative z-10')}
              rel="noreferrer"
            >
              Promo
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
